import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form, Space, Typography } from 'antd';
import { useAssetTypesQuery } from 'pos-client/components/assetsTypes/hooks/useAssetTypesQuery.tsx';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { PosInput } from 'pos-client/components/shared/Form/PosInput.tsx';
import { PosSelect } from 'pos-client/components/shared/Form/PosSelect.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type BaseAssetTypeModel,
  type CreateAssetTypeModel,
  CreateAssetTypesSchema,
  type UpdateAssetTypeModel,
  UpdateAssetTypesSchema,
} from 'pos-shared/models/assetTypesSchema.ts';
import z from 'zod';

interface AssetModalProps {
  data?: BaseAssetTypeModel | undefined;
  handleOk: () => void;
  handleCancel: () => void;
}

const MAX_FIELD_LENGTHS = {
  long: 100,
  short: 25,
};

export const AssetTypesModal = ({
  data,
  handleOk,
  handleCancel,
}: AssetModalProps) => {
  const { t } = useTranslation();
  const { invalidateAllAssets } = useAssetTypesQuery({});
  const { showSuccess } = useNotifications();

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    watch,
  } = useForm<CreateAssetTypeModel | UpdateAssetTypeModel>({
    mode: 'all',
    shouldFocusError: true,
    defaultValues: {
      ...(data && data),
    },
    resolver: zodResolver(
      z.union([CreateAssetTypesSchema, UpdateAssetTypesSchema])
    ),
  });

  const { Title } = Typography;
  // Implemented to fix maximum depth error for useState in useEffect
  const mode = useMemo(() => (data ? 'edit' : 'create'), [data]);

  const { showModal: showUnsavedChangesModal } = useConfirmModal({
    type: 'delete',
    title: t('common.confirmCancel.unsavedChanges'),
    content: t('common.confirmCancel.unsavedChangesConfirm'),
    confirmText: t('common.buttons.discardChanges'),
    confirmProps: {
      ghost: true,
    },
    onConfirm: () => {
      reset();
      handleCancel();
    },
  });

  const cancelModal = () => {
    if (isDirty) {
      showUnsavedChangesModal();
    } else {
      reset();
      handleCancel();
    }
  };

  const onSuccessMutation = async () => {
    showSuccess(t('assets.assetTypeAdded'));
    handleOk();
    await invalidateAllAssets();
    reset();
  };

  const { mutate: createAssetType, isLoading: isCreating } =
    trpc.createAssetType.useMutation({
      onSuccess: onSuccessMutation,
    });

  const { mutate: updateAssetType, isLoading: isUpdating } =
    trpc.updateAssetType.useMutation({
      onSuccess: onSuccessMutation,
    });

  const handleSaveAsset = handleSubmit(
    (formData: CreateAssetTypeModel | UpdateAssetTypeModel) => {
      if (mode === 'edit') {
        updateAssetType({ id: data?.id || '', ...formData });
      } else {
        // @ts-ignore
        createAssetType(formData);
      }
    }
  );

  const serviceTypes = [
    {
      name: 'Mobile',
      id: 'Mobile',
    },
    {
      name: 'Static',
      id: 'Static',
    },
  ];

  return (
    <PosModal
      header={{
        title:
          mode === 'edit'
            ? t('assets.editAssetType')
            : t('assets.createAssetType'),
      }}
      open
      onCancel={cancelModal}
      footer={
        <Space>
          <Button
            type="default"
            onClick={cancelModal}
            style={{ marginLeft: 'auto' }}
          >
            {t('common.buttons.cancel')}
          </Button>

          <Button
            key="submit"
            type="primary"
            onClick={handleSaveAsset}
            loading={isCreating || isUpdating}
            disabled={!isDirty || !isValid}
          >
            {mode === 'edit'
              ? t('common.buttons.update')
              : t('common.buttons.create')}
          </Button>
        </Space>
      }
    >
      {mode === 'edit' && <Title level={5}>{watch()?.name}</Title>}

      <Form layout="vertical">
        <PosInput
          control={control}
          name="name"
          label={t('assets.assetTypeName')}
          maxLength={MAX_FIELD_LENGTHS.long}
        />
        <PosInput
          control={control}
          name="description"
          label={t('common.words.description')}
          maxLength={MAX_FIELD_LENGTHS.long}
        />
        <PosSelect
          control={control}
          name="serviceType"
          label={t('assets.assetTypeServiceType')}
          options={serviceTypes}
          disabled={mode === 'edit'}
          optionFilterProp="serviceType"
          fieldNames={{
            label: 'name',
            value: 'id',
          }}
        />
      </Form>
    </PosModal>
  );
};
